// src/components/ChatBot/ChatBot.jsx
// Ordinateur : aigle + panneau vitré "Aurore"  |  Mobile (≤ 640px) : panneau qui monte du bas.
//
// Mascotte : une VRAIE image d'aigle (PNG/WebP/GIF transparent), à placer ici :
//     public/eagle-hd.webp        (ou change EAGLE_SRC ci-dessous)
//   - l'aigle plane dans son coin, bat des ailes plus vite quand le bot répond
//   - NOUVEAU : il suit la position de la souris (il se tourne vers le curseur et se penche vers lui)
//   - chat fermé : il s'envole à travers l'écran (jamais sous la navbar), se retourne selon
//     sa direction, puis revient se poser dans son coin
//   - option "planche de sprites" (battement d'ailes) : mets EAGLE_FRAMES > 1
//
// Pages Login / Signup :  <ChatBot mode="public" />
// Espace connecté      :  <ChatBot mode="member" roam />      (roam={false} pour désactiver le vol)
import React, { useState, useRef, useEffect } from "react";
import {
  FaTimes,
  FaPaperPlane,
  FaUserPlus,
  FaGem,
  FaWallet,
  FaHeadset,
  FaRedo,
  FaRegCopy,
  FaCheck,
} from "react-icons/fa";
import "./chatbot.css";

const API_URL = process.env.REACT_APP_API_URL; // ex: http://localhost:5000/api
const EAGLE_SRC = `${process.env.PUBLIC_URL}/eagle-hd.webp`;
const EAGLE_FRAMES = 1;
// ---------- Réglages de l'aigle ----------

const EAGLE_FACES_RIGHT = false; // true si l'aigle de ton image regarde vers la DROITE, false si vers la GAUCHE

const TEXTS = {
  public: {
    title: "MWGTD Assistant",
    welcome:
      "Hi! I can explain how MWGTD works before you create your account. What would you like to know?",
  },
  member: {
    title: "MWGTD Assistant",
    welcome: "Hi! How can I help you with the platform today?",
  },
};

const QUICK = [
  { label: "How to sign up", Icon: FaUserPlus },
  { label: "Subscription", Icon: FaGem },
  { label: "Withdrawals", Icon: FaWallet },
  { label: "Contact support", Icon: FaHeadset },
];

// Version arabe : affichée automatiquement si le navigateur du visiteur est en arabe.
const IS_AR =
  typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("ar");
const TEXTS_AR = {
  public: {
    title: "مساعد MWGTD",
    welcome: "مرحبًا! يمكنني أن أشرح لك كيف تعمل MWGTD قبل إنشاء حسابك. ماذا تريد أن تعرف؟",
  },
  member: {
    title: "مساعد MWGTD",
    welcome: "مرحبًا! كيف يمكنني مساعدتك في المنصة اليوم؟",
  },
};
const QUICK_AR = [
  { label: "كيف أسجل", Icon: FaUserPlus },
  { label: "الاشتراك", Icon: FaGem },
  { label: "السحب", Icon: FaWallet },
  { label: "التواصل مع الدعم", Icon: FaHeadset },
];

const UI = IS_AR
  ? {
      status: "آلي · يرد فورًا",
      newChat: "محادثة جديدة",
      closeAria: "إغلاق المساعد",
      openAria: "فتح المساعد",
      invite: "هل تحتاج مساعدة؟",
      note: "لا تشارك كلمة المرور أبدًا في هذه المحادثة.",
      placeholder: "اكتب سؤالك…",
      copy: "نسخ",
      copied: "تم النسخ",
      error: "خطأ في الاتصال. حاول مرة أخرى.",
    }
  : {
      status: "Automated · replies instantly",
      newChat: "New conversation",
      closeAria: "Close assistant",
      openAria: "Open assistant",
      invite: "Need help? Ask me",
      note: "Never share your password in this chat.",
      placeholder: "Type your question…",
      copy: "Copy",
      copied: "Copied",
      error: "Connection error. Please try again.",
    };

// ---------- sessionStorage sécurisé (peut être indisponible) ----------
const store = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* ignore */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  },
};

// ---------- Mascotte : vrai aigle (image) ----------
// busy : l'aigle bat des ailes plus vite pendant que le bot répond
const Eagle = ({ className = "mwg-bot__eagle", busy = false }) => (
  <span className={`${className} ${busy ? "is-busy" : ""}`} aria-hidden="true">
    {EAGLE_FRAMES > 1 ? (
      <span
        className="mwg-bot__sprite"
        style={{ backgroundImage: `url(${EAGLE_SRC})`, "--frames": EAGLE_FRAMES }}
      />
    ) : (
      <img src={EAGLE_SRC} alt="" draggable="false" />
    )}
  </span>
);

// ---------- Rendu markdown léger (sans dangerouslySetInnerHTML) ----------
const renderInline = (text, keyPrefix) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
      <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
    )
  );

const RichText = ({ text }) => {
  const lines = String(text).split("\n");
  const blocks = [];
  let list = null;

  const flush = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag key={`l${blocks.length}`} className="mwg-bot__ul">
        {list.items.map((it, i) => (
          <li key={i}>{renderInline(it, `li${blocks.length}-${i}`)}</li>
        ))}
      </Tag>
    );
    list = null;
  };

  lines.forEach((line, idx) => {
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = !!numbered;
      if (!list || list.ordered !== ordered) {
        flush();
        list = { ordered, items: [] };
      }
      list.items.push((bullet || numbered)[1]);
    } else {
      flush();
      if (line.trim() === "") {
        if (blocks.length) blocks.push(<div key={`s${idx}`} className="mwg-bot__gap" />);
      } else {
        blocks.push(<div key={`p${idx}`}>{renderInline(line, `p${idx}`)}</div>);
      }
    }
  });
  flush();
  return <>{blocks}</>;
};

const ChatBot = ({ mode = "public", roam = true }) => {
  const texts = IS_AR ? TEXTS_AR : TEXTS;
  const t = texts[mode] || texts.public;
  const quick = IS_AR ? QUICK_AR : QUICK;

  const KEY_MSGS = `mwg-bot:${mode}:messages`;
  const KEY_INVITE = "mwg-bot:invite-seen";

  const welcomeMsg = { role: "assistant", content: t.welcome, welcome: true };

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = store.get(KEY_MSGS);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch (e) {
        /* ignore */
      }
    }
    return [welcomeMsg];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);

  const hasConversation = messages.length > 1;

  // Sauvegarde de l'historique pendant la session
  useEffect(() => {
    store.set(KEY_MSGS, JSON.stringify(messages));
  }, [messages, KEY_MSGS]);

  // Bulle d'invitation : une seule fois par session, jamais si le chat a déjà servi
  useEffect(() => {
    if (store.get(KEY_INVITE) || hasConversation) return;
    const show = setTimeout(() => {
      setShowInvite(true);
      store.set(KEY_INVITE, "1");
    }, 4000);
    return () => clearTimeout(show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showInvite) return;
    const hide = setTimeout(() => setShowInvite(false), 9000);
    return () => clearTimeout(hide);
  }, [showInvite]);

  // Défilement automatique vers le dernier message
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading, open]);

  // Échap = fermer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Mobile : on bloque le défilement de la page derrière le panneau
  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    if (open && isMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // ---------- L'aigle suit la souris ----------
  // Chat fermé, ordinateur uniquement : l'aigle se tourne vers le curseur (sauf pendant un vol,
  // où il regarde dans le sens du vol) et se penche / se décale légèrement dans sa direction.
  // Valeurs écrites dans --px / --py (-1 à 1), lues par le CSS.
  useEffect(() => {
    const el = fabRef.current;
    if (open || !el) return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const clamp = (v) => Math.max(-1, Math.min(1, v));
    const apply = (px, py) => {
      el.style.setProperty("--px", px.toFixed(3));
      el.style.setProperty("--py", py.toFixed(3));
    };
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect(); // tient compte de la position actuelle (même en vol)
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        apply(clamp(dx / 500), clamp(dy / 500));
        // il regarde le curseur (zone morte de 40px pour éviter qu'il clignote d'un côté à l'autre)
        if (!el.dataset.walking && Math.abs(dx) > 40) {
          el.style.setProperty("--face", dx > 0 === EAGLE_FACES_RIGHT ? "1" : "-1");
        }
      });
    };
    const onLeave = (e) => {
      if (!e.relatedTarget) apply(0, 0); // la souris quitte la fenêtre : il se redresse
    };
    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseout", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseout", onLeave);
      apply(0, 0);
    };
  }, [open]);

  // ---------- Vol de l'aigle (chat fermé) ----------
  // Toutes les 12-24 s : 2 ou 3 arrêts au hasard sur l'écran (jamais sous la navbar), puis retour au coin.
  // L'aigle se retourne selon sa direction. Survol = il se pose puis rentre. Ouverture du chat = retour immédiat.
  // Désactivé : sur mobile, avec prefers-reduced-motion, ou si roam={false}.
  useEffect(() => {
    const el = fabRef.current;
    if (!roam || open || !el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(max-width: 640px)").matches) return;

    const TOP = 112; // hauteur de la navbar à ne pas recouvrir
    const M = 12; // marge au bord gauche
    const SPEED = 260; // px / seconde (vol)
    let timers = [];
    let home = null;
    const later = (fn, ms) => {
      timers.push(setTimeout(fn, ms));
    };
    const clear = () => {
      timers.forEach(clearTimeout);
      timers = [];
    };
    const rand = (a, b) => a + Math.random() * (b - a);

    // La tête suit TOUJOURS le sens réel du déplacement (mesuré à chaque image),
    // quelle que soit la courbe d'animation, un arrêt au survol ou le retour au coin.
    let trackRaf = 0;
    let lastX = null;
    const track = () => {
      if (!el.dataset.walking) {
        trackRaf = 0;
        lastX = null;
        return;
      }
      const left = el.getBoundingClientRect().left;
      if (lastX !== null) {
        const dx = left - lastX;
        if (Math.abs(dx) > 0.3) {
          el.style.setProperty("--face", dx > 0 === EAGLE_FACES_RIGHT ? "1" : "-1");
          el.style.setProperty("--tilt", dx > 0 ? "6deg" : "-6deg");
        }
      }
      lastX = left;
      trackRaf = requestAnimationFrame(track);
    };
    const startTrack = () => {
      if (!trackRaf) {
        lastX = null;
        trackRaf = requestAnimationFrame(track);
      }
    };

    // décalage actuel par rapport à la position d'origine
    const offset = () => {
      const r = el.getBoundingClientRect();
      return { x: r.left - home.left, y: r.top - home.top };
    };
    const moveTo = (x, y) => {
      const c = offset();
      const dur = Math.max(0.8, Math.hypot(x - c.x, y - c.y) / SPEED);
      const goingRight = x >= c.x;
      el.style.setProperty("--rdur", `${dur}s`);
      el.style.setProperty("--rx", `${x}px`);
      el.style.setProperty("--ry", `${y}px`);
      el.style.setProperty("--tilt", goingRight ? "6deg" : "-6deg");
      // l'aigle regarde dans le sens du vol
      el.style.setProperty("--face", goingRight === EAGLE_FACES_RIGHT ? "1" : "-1");
      el.dataset.walking = "1";
      startTrack();
      return dur * 1000;
    };
    const goHome = () => {
      const ms = moveTo(0, 0);
      later(() => {
        delete el.dataset.walking;
        el.style.setProperty("--face", EAGLE_FACES_RIGHT ? "1" : "-1");
        later(startTour, rand(12000, 24000));
      }, ms + 100);
    };
    const step = (n) => {
      if (n === 0) return goHome();
      const x = -Math.random() * Math.max(0, home.left - M);
      const y = -Math.random() * Math.max(0, home.top - TOP);
      const ms = moveTo(x, y);
      later(() => {
        delete el.dataset.walking; // il plane un instant
        later(() => step(n - 1), rand(1500, 3000));
      }, ms);
    };
    function startTour() {
      const r = el.getBoundingClientRect();
      home = { left: r.left, top: r.top };
      step(2 + Math.floor(Math.random() * 2));
    }

    // survol : il s'arrête sur place, puis rentre
    const onEnter = () => {
      if (!home) return;
      const c = offset();
      if (Math.abs(c.x) < 2 && Math.abs(c.y) < 2) return;
      clear();
      el.style.setProperty("--rdur", "0s");
      el.style.setProperty("--rx", `${c.x}px`);
      el.style.setProperty("--ry", `${c.y}px`);
      delete el.dataset.walking;
      later(goHome, 3000);
    };
    el.addEventListener("pointerenter", onEnter);

    later(startTour, 12000); // premier vol, après la bulle d'invitation

    return () => {
      clear();
      cancelAnimationFrame(trackRaf);
      el.removeEventListener("pointerenter", onEnter);
      el.style.setProperty("--rdur", "0s");
      el.style.setProperty("--rx", "0px");
      el.style.setProperty("--ry", "0px");
      el.style.setProperty("--face", EAGLE_FACES_RIGHT ? "1" : "-1");
      delete el.dataset.walking;
    };
  }, [open, roam]);

  const toggleOpen = () => {
    setShowInvite(false);
    setOpen((o) => !o);
  };

  const newConversation = () => {
    if (loading) return;
    store.remove(KEY_MSGS);
    setMessages([welcomeMsg]);
    setInput("");
    setCopiedIdx(null);
  };

  const copyMessage = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1500);
    } catch (e) {
      /* ignore */
    }
  };

  const sendText = async (raw) => {
    const text = (raw || "").trim();
    if (!text || loading) return;

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const headers = { "Content-Type": "application/json" };
      if (mode === "member") headers.Authorization = `Bearer ${localStorage.getItem("token")}`;

      const res = await fetch(`${API_URL}/chatbot/${mode}`, {
        method: "POST",
        headers,
        // on n'envoie pas le message d'accueil (il vient du bot)
        body: JSON.stringify({ messages: next.slice(1).map(({ role, content }) => ({ role, content })) }),
      });
      const data = await res.json();
      setMessages([...next, { role: "assistant", content: data.reply || data.error || "Error." }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: UI.error }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mwg-bot" style={{ "--face": EAGLE_FACES_RIGHT ? 1 : -1 }}>
      {open && (
        <>
          {/* Fond assombri : visible uniquement sur mobile, un clic ferme le panneau */}
          <div className="mwg-bot__overlay" onClick={() => setOpen(false)} aria-hidden="true" />

          <div className="mwg-bot__panel" role="dialog" aria-label={t.title}>
            <div className="mwg-bot__inner">
              <div className="mwg-bot__handle" aria-hidden="true" />

              <div className="mwg-bot__head">
                <span className="mwg-bot__orb" aria-hidden="true">
                  <Eagle className="mwg-bot__eagle-sm" busy={loading} />
                </span>
                <div className="mwg-bot__headtext">
                  <strong>{t.title}</strong>
                  <small>{UI.status}</small>
                </div>
                {hasConversation && (
                  <button
                    type="button"
                    className="mwg-bot__x"
                    onClick={newConversation}
                    disabled={loading}
                    aria-label={UI.newChat}
                    title={UI.newChat}
                  >
                    <FaRedo />
                  </button>
                )}
                <button
                  type="button"
                  className="mwg-bot__x"
                  onClick={() => setOpen(false)}
                  aria-label={UI.closeAria}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="mwg-bot__list" aria-live="polite">
                {messages.map((m, i) => (
                  <div key={i} className={`mwg-bot__row mwg-bot__row--${m.role}`}>
                    <div dir="auto" className={`mwg-bot__msg mwg-bot__msg--${m.role}`}>
                      {m.role === "assistant" ? <RichText text={m.content} /> : m.content}
                    </div>
                    {m.role === "assistant" && !m.welcome && (
                      <button
                        type="button"
                        className="mwg-bot__copy"
                        onClick={() => copyMessage(m.content, i)}
                        aria-label={UI.copy}
                      >
                        {copiedIdx === i ? <FaCheck /> : <FaRegCopy />}
                        <span>{copiedIdx === i ? UI.copied : UI.copy}</span>
                      </button>
                    )}
                  </div>
                ))}

                {/* Cartes d'accueil : seulement avant le premier message */}
                {!hasConversation && !loading && (
                  <div className="mwg-bot__cards">
                    {quick.map(({ label, Icon }) => (
                      <button key={label} type="button" className="mwg-bot__card" onClick={() => sendText(label)}>
                        <span className="mwg-bot__cardicon" aria-hidden="true">
                          <Icon />
                        </span>
                        <span className="mwg-bot__cardlabel">{label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {loading && (
                  <div className="mwg-bot__msg mwg-bot__msg--assistant mwg-bot__typing" aria-label="Typing">
                    <i />
                    <i />
                    <i />
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Pilules compactes : une fois la conversation lancée */}
              {hasConversation && (
                <div className="mwg-bot__chips">
                  {quick.map(({ label }) => (
                    <button key={label} type="button" disabled={loading} onClick={() => sendText(label)}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              <div className="mwg-bot__note">{UI.note}</div>

              {/* pas de <form> : évite de déclencher le submit de la page login/signup */}
              <div className="mwg-bot__form">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      sendText(input);
                    }
                  }}
                  dir="auto"
                  placeholder={UI.placeholder}
                  aria-label="Message"
                  maxLength={500}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => sendText(input)}
                  disabled={loading || !input.trim()}
                  aria-label="Send"
                >
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bulle d'invitation */}
      {!open && showInvite && (
        <button type="button" className="mwg-bot__invite" onClick={toggleOpen}>
          {UI.invite}
        </button>
      )}

      {/* Bouton flottant = l'aigle */}
      <button
        ref={fabRef}
        type="button"
        className={`mwg-bot__fab ${open ? "is-open" : ""}`}
        onClick={toggleOpen}
        aria-expanded={open}
        aria-label={open ? UI.closeAria : UI.openAria}
      >
        {open ? <FaTimes /> : <Eagle busy={loading} />}
        {!open && <span className="mwg-bot__dot" aria-hidden="true" />}
      </button>
    </div>
  );
};

export default ChatBot;